import { useEffect, useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ChevronLeft, ChevronRight, TrendingUp, Wine, Bed, CreditCard, Users, Download, Search, Building2 } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, LineChart, Line,
} from "recharts";
import { getMonthlyRevenue, getRevenueByGuest, getRevenueByPartner, downloadCsv, fmt, fmtDate } from "@/lib/api";
import ReservationDrawer from "@/components/ReservationDrawer";

export default function Revenue() {
  const [tab, setTab] = useState("by-guest");
  const [chartsReady, setChartsReady] = useState(false);
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState({ months: [], totals: {}, year });
  const [guestData, setGuestData] = useState({ guests: [], count: 0 });
  const [partnerData, setPartnerData] = useState({ partners: [], count: 0 });
  const [guestSearch, setGuestSearch] = useState("");
  const [partnerSearch, setPartnerSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [partnerDateFrom, setPartnerDateFrom] = useState("");
  const [partnerDateTo, setPartnerDateTo] = useState("");
  const [selectedRes, setSelectedRes] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const guestQueryParams = useMemo(() => {
    const p = {};
    if (guestSearch) p.search = guestSearch;
    if (dateFrom) p.date_from = dateFrom;
    if (dateTo) p.date_to = dateTo;
    return p;
  }, [guestSearch, dateFrom, dateTo]);

  useEffect(() => {
    getMonthlyRevenue(year).then(setData);
  }, [year]);

  useEffect(() => {
    const t = setTimeout(() => {
      getRevenueByGuest(guestQueryParams).then(setGuestData);
    }, 150);
    return () => clearTimeout(t);
  }, [guestQueryParams]);

  const partnerQueryParams = useMemo(() => {
    const p = {};
    if (partnerSearch) p.search = partnerSearch;
    if (partnerDateFrom) p.date_from = partnerDateFrom;
    if (partnerDateTo) p.date_to = partnerDateTo;
    return p;
  }, [partnerSearch, partnerDateFrom, partnerDateTo]);

  useEffect(() => {
    const t = setTimeout(() => {
      getRevenueByPartner(partnerQueryParams).then(setPartnerData);
    }, 150);
    return () => clearTimeout(t);
  }, [partnerQueryParams]);

  const applyMonth = (offset = 0) => {
    // offset = 0 → current month; -1 → previous month; etc.
    const d = new Date();
    d.setMonth(d.getMonth() + offset, 1);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const lastDay = new Date(y, d.getMonth() + 1, 0).getDate();
    setDateFrom(`${y}-${m}-01`);
    setDateTo(`${y}-${m}-${String(lastDay).padStart(2, "0")}`);
  };

  const applyYear = () => {
    const y = new Date().getFullYear();
    setDateFrom(`${y}-01-01`);
    setDateTo(`${y}-12-31`);
  };

  const clearDates = () => { setDateFrom(""); setDateTo(""); };

  // Wait one animation frame after switching to Monthly so ResponsiveContainer sees a non-zero width
  useEffect(() => {
    if (tab === "monthly") {
      setChartsReady(false);
      const id = requestAnimationFrame(() => {
        requestAnimationFrame(() => setChartsReady(true));
      });
      return () => cancelAnimationFrame(id);
    }
    setChartsReady(false);
  }, [tab]);

  const bestMonth = useMemo(() => {
    if (!data.months?.length) return null;
    return [...data.months].sort((a, b) => b.total - a.total)[0];
  }, [data]);

  const years = [year - 1, year, year + 1];

  // KPIs — prefer by-guest totals since the user wants guest-first view
  const guestTotals = useMemo(() => {
    const t = { total: 0, paid: 0, remaining: 0, guests: guestData.guests.length, reservations: 0 };
    for (const g of guestData.guests) {
      t.total += g.total;
      t.paid += g.paid;
      t.remaining += g.remaining;
      t.reservations += g.reservations;
    }
    return t;
  }, [guestData]);

  const stats = [
    { label: "Unique Guests", value: guestTotals.guests, icon: Users, accent: "text-blue-600", bg: "bg-blue-50" },
    { label: "Total Revenue", value: fmt(guestTotals.total), icon: TrendingUp, accent: "text-emerald-600", bg: "bg-emerald-50" },
    { label: "Paid", value: fmt(guestTotals.paid), icon: CreditCard, accent: "text-violet-600", bg: "bg-violet-50" },
    { label: "Outstanding", value: fmt(guestTotals.remaining), icon: Bed, accent: "text-red-600", bg: "bg-red-50" },
  ];

  return (
    <div className="p-8 space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900">Revenue</h1>
          <p className="text-sm text-slate-500 mt-1">Guest-level revenue breakdown and monthly trends</p>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="revenue-kpis">
        {stats.map(s => {
          const Icon = s.icon;
          return (
            <Card key={s.label} className="p-5 shadow-none border-slate-200">
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-500">{s.label}</div>
                  <div className="font-heading text-2xl font-bold text-slate-900 mt-2">{s.value}</div>
                </div>
                <div className={`h-9 w-9 rounded-md ${s.bg} flex items-center justify-center`}>
                  <Icon size={17} className={s.accent} strokeWidth={1.75} />
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      <Card className="shadow-none border-slate-200 overflow-hidden">
        <Tabs value={tab} onValueChange={setTab}>
          <div className="flex items-center justify-between border-b border-slate-200 pr-4">
            <TabsList className="h-12 bg-white rounded-none gap-0 p-0">
              <TabsTrigger
                value="by-guest"
                className="h-full rounded-none px-5 data-[state=active]:bg-blue-50 data-[state=active]:text-blue-700 data-[state=active]:shadow-none border-b-2 border-transparent data-[state=active]:border-blue-600"
                data-testid="revenue-tab-by-guest"
              >
                <Users size={14} className="mr-1.5" /> By Guest
              </TabsTrigger>
              <TabsTrigger
                value="by-partner"
                className="h-full rounded-none px-5 data-[state=active]:bg-blue-50 data-[state=active]:text-blue-700 data-[state=active]:shadow-none border-b-2 border-transparent data-[state=active]:border-blue-600"
                data-testid="revenue-tab-by-partner"
              >
                <Building2 size={14} className="mr-1.5" /> By Partner
              </TabsTrigger>
              <TabsTrigger
                value="monthly"
                className="h-full rounded-none px-5 data-[state=active]:bg-blue-50 data-[state=active]:text-blue-700 data-[state=active]:shadow-none border-b-2 border-transparent data-[state=active]:border-blue-600"
                data-testid="revenue-tab-monthly"
              >
                <TrendingUp size={14} className="mr-1.5" /> Monthly
              </TabsTrigger>
            </TabsList>
          </div>

          {/* By Guest */}
          <TabsContent value="by-guest" className="m-0 p-0">
            <div className="p-5 border-b border-slate-200 space-y-3">
              <div className="flex items-center gap-3 flex-wrap">
                <div className="relative flex-1 min-w-[220px]">
                  <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    placeholder="Search guest..."
                    value={guestSearch}
                    onChange={(e) => setGuestSearch(e.target.value)}
                    className="pl-9 h-10"
                    data-testid="guest-search-input"
                  />
                </div>
                <Button
                  variant="outline"
                  onClick={() => {
                    const p = { ...guestQueryParams, mode: "summary" };
                    const stamp = (dateFrom || dateTo)
                      ? `_${dateFrom || "start"}_to_${dateTo || new Date().toISOString().slice(0,10)}`
                      : "";
                    downloadCsv(
                      "/revenue/by-guest.csv",
                      p,
                      `revenue_by_guest_summary${stamp}_${new Date().toISOString().slice(0,10)}.csv`,
                    );
                  }}
                  className="h-10"
                  data-testid="guest-csv-summary-btn"
                  title="One row per guest with category breakdown"
                >
                  <Download size={14} /> Summary CSV
                </Button>
                <Button
                  onClick={() => {
                    const p = { ...guestQueryParams, mode: "detailed" };
                    const stamp = (dateFrom || dateTo)
                      ? `_${dateFrom || "start"}_to_${dateTo || new Date().toISOString().slice(0,10)}`
                      : "";
                    downloadCsv(
                      "/revenue/by-guest.csv",
                      p,
                      `revenue_by_guest_detailed${stamp}_${new Date().toISOString().slice(0,10)}.csv`,
                    );
                  }}
                  className="h-10 bg-slate-900 hover:bg-slate-800 text-white"
                  data-testid="guest-csv-detailed-btn"
                  title="Per-payment, per-line-item rows for accounting & reconciliation"
                >
                  <Download size={14} /> Detailed CSV
                </Button>
              </div>

              <div className="flex items-center gap-2 flex-wrap">
                <div className="text-xs uppercase tracking-wider text-slate-500 mr-1">Check-in</div>
                <Input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="h-9 w-[160px]"
                  data-testid="guest-date-from"
                />
                <span className="text-slate-400 text-sm">→</span>
                <Input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="h-9 w-[160px]"
                  data-testid="guest-date-to"
                />
                <div className="h-5 w-px bg-slate-200 mx-1" />
                <Button size="sm" variant="ghost" onClick={() => applyMonth(0)} className="h-9 text-xs" data-testid="guest-preset-this-month">This month</Button>
                <Button size="sm" variant="ghost" onClick={() => applyMonth(-1)} className="h-9 text-xs" data-testid="guest-preset-last-month">Last month</Button>
                <Button size="sm" variant="ghost" onClick={applyYear} className="h-9 text-xs" data-testid="guest-preset-ytd">This year</Button>
                {(dateFrom || dateTo) && (
                  <Button size="sm" variant="ghost" onClick={clearDates} className="h-9 text-xs text-slate-500" data-testid="guest-preset-clear">Clear</Button>
                )}
                <div className="text-xs text-slate-400 ml-auto" data-testid="guest-count-label">{guestData.count} guest{guestData.count === 1 ? "" : "s"} in range</div>
              </div>
            </div>

            <Table data-testid="by-guest-table">
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>Guest</TableHead>
                  <TableHead className="text-center w-20">Stays</TableHead>
                  <TableHead>Last Check-in</TableHead>
                  <TableHead className="text-right">Accommodation</TableHead>
                  <TableHead className="text-right">Consumptions</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead className="text-right">Paid</TableHead>
                  <TableHead className="text-right">Outstanding</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {guestData.guests.map((g, idx) => (
                  <TableRow key={`${g.guest_name}-${idx}`} className="border-slate-100" data-testid={`guest-row-${idx}`}>
                    <TableCell>
                      <div className="font-medium text-slate-900">{g.guest_name}</div>
                      {g.guest_email && <div className="text-xs text-slate-500">{g.guest_email}</div>}
                    </TableCell>
                    <TableCell className="text-center">
                      <span className="inline-flex items-center justify-center h-6 w-8 rounded bg-slate-100 text-xs font-semibold">{g.reservations}</span>
                    </TableCell>
                    <TableCell className="text-slate-600 text-sm">{fmtDate(g.last_check_in)}</TableCell>
                    <TableCell className="text-right text-slate-900">{fmt(g.accommodation)}</TableCell>
                    <TableCell className="text-right text-slate-900">{fmt(g.consumption)}</TableCell>
                    <TableCell className="text-right font-semibold text-slate-900">{fmt(g.total)}</TableCell>
                    <TableCell className="text-right text-emerald-700">{fmt(g.paid)}</TableCell>
                    <TableCell className={`text-right font-semibold ${g.remaining > 0.01 ? "text-red-600" : "text-slate-400"}`}>{fmt(g.remaining)}</TableCell>
                  </TableRow>
                ))}
                {guestData.guests.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-12 text-slate-400 text-sm">
                      No guests yet
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TabsContent>

          {/* By Partner */}
          <TabsContent value="by-partner" className="m-0 p-0">
            <div className="p-5 border-b border-slate-200 space-y-3">
              <div className="flex items-center gap-3 flex-wrap">
                <div className="relative flex-1 min-w-[220px]">
                  <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    placeholder="Search partner..."
                    value={partnerSearch}
                    onChange={(e) => setPartnerSearch(e.target.value)}
                    className="pl-9 h-10"
                    data-testid="partner-search-input"
                  />
                </div>
                <Button
                  variant="outline"
                  onClick={() => {
                    const stamp = (partnerDateFrom || partnerDateTo)
                      ? `_${partnerDateFrom || "start"}_to_${partnerDateTo || new Date().toISOString().slice(0,10)}`
                      : "";
                    downloadCsv(
                      "/revenue/by-partner.csv",
                      { ...partnerQueryParams, mode: "summary" },
                      `revenue_by_partner_summary${stamp}_${new Date().toISOString().slice(0,10)}.csv`,
                    );
                  }}
                  className="h-10"
                  data-testid="partner-csv-summary-btn"
                  title="One row per partner with totals"
                >
                  <Download size={14} /> Summary CSV
                </Button>
                <Button
                  onClick={() => {
                    const stamp = (partnerDateFrom || partnerDateTo)
                      ? `_${partnerDateFrom || "start"}_to_${partnerDateTo || new Date().toISOString().slice(0,10)}`
                      : "";
                    downloadCsv(
                      "/revenue/by-partner.csv",
                      { ...partnerQueryParams, mode: "detailed" },
                      `revenue_by_partner_detailed${stamp}_${new Date().toISOString().slice(0,10)}.csv`,
                    );
                  }}
                  className="h-10 bg-slate-900 hover:bg-slate-800 text-white"
                  data-testid="partner-csv-detailed-btn"
                  title="Per-cost, per-payment rows for accounting & reconciliation"
                >
                  <Download size={14} /> Detailed CSV
                </Button>
              </div>

              <div className="flex items-center gap-2 flex-wrap">
                <div className="text-xs uppercase tracking-wider text-slate-500 mr-1">Start date</div>
                <Input
                  type="date"
                  value={partnerDateFrom}
                  onChange={(e) => setPartnerDateFrom(e.target.value)}
                  className="h-9 w-[160px]"
                  data-testid="partner-date-from"
                />
                <span className="text-slate-400 text-sm">→</span>
                <Input
                  type="date"
                  value={partnerDateTo}
                  onChange={(e) => setPartnerDateTo(e.target.value)}
                  className="h-9 w-[160px]"
                  data-testid="partner-date-to"
                />
                <div className="h-5 w-px bg-slate-200 mx-1" />
                <Button size="sm" variant="ghost" onClick={() => { const d = new Date(); const y = d.getFullYear(); const m = String(d.getMonth() + 1).padStart(2, "0"); const last = new Date(y, d.getMonth() + 1, 0).getDate(); setPartnerDateFrom(`${y}-${m}-01`); setPartnerDateTo(`${y}-${m}-${String(last).padStart(2,"0")}`); }} className="h-9 text-xs" data-testid="partner-preset-this-month">This month</Button>
                <Button size="sm" variant="ghost" onClick={() => { const d = new Date(); d.setMonth(d.getMonth() - 1, 1); const y = d.getFullYear(); const m = String(d.getMonth() + 1).padStart(2,"0"); const last = new Date(y, d.getMonth() + 1, 0).getDate(); setPartnerDateFrom(`${y}-${m}-01`); setPartnerDateTo(`${y}-${m}-${String(last).padStart(2,"0")}`); }} className="h-9 text-xs" data-testid="partner-preset-last-month">Last month</Button>
                <Button size="sm" variant="ghost" onClick={() => { const y = new Date().getFullYear(); setPartnerDateFrom(`${y}-01-01`); setPartnerDateTo(`${y}-12-31`); }} className="h-9 text-xs" data-testid="partner-preset-ytd">This year</Button>
                {(partnerDateFrom || partnerDateTo) && (
                  <Button size="sm" variant="ghost" onClick={() => { setPartnerDateFrom(""); setPartnerDateTo(""); }} className="h-9 text-xs text-slate-500" data-testid="partner-preset-clear">Clear</Button>
                )}
                <div className="text-xs text-slate-400 ml-auto" data-testid="partner-count-label">{partnerData.count} partner{partnerData.count === 1 ? "" : "s"} in range</div>
              </div>
            </div>
            <Table>
              <TableHeader className="bg-slate-50/50">
                <TableRow className="border-slate-200 hover:bg-transparent">
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Partner</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold text-center">Retreats</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold text-center">Pax</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Last start</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold text-right">Total</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold text-right">Paid</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold text-right">Remaining</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {partnerData.partners.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-12 text-slate-400 italic">No partner activity in this range</TableCell>
                  </TableRow>
                ) : partnerData.partners.map((p) => (
                  <TableRow key={p.partner_id || p.partner_name} className="border-slate-100" data-testid={`partner-row-${p.partner_id}`}>
                    <TableCell>
                      <div className="font-medium text-slate-900">{p.partner_name}</div>
                      {p.contact && <div className="text-[11px] text-slate-500">{p.contact}</div>}
                    </TableCell>
                    <TableCell className="text-center">{p.retreats}</TableCell>
                    <TableCell className="text-center">{p.participants}</TableCell>
                    <TableCell className="text-xs text-slate-500">{fmtDate(p.last_start_date) || "—"}</TableCell>
                    <TableCell className="text-right font-medium tabular-nums">{fmt(p.total)}</TableCell>
                    <TableCell className="text-right tabular-nums text-emerald-700">{fmt(p.paid)}</TableCell>
                    <TableCell className={`text-right tabular-nums font-medium ${p.remaining > 0.01 ? "text-amber-700" : "text-slate-400"}`}>{fmt(p.remaining)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TabsContent>

          {/* Monthly */}
          <TabsContent value="monthly" className="m-0 p-0">
            <div className="p-5 border-b border-slate-200 flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-1">
                <Button variant="outline" size="sm" onClick={() => setYear(y => y - 1)} data-testid="revenue-year-prev"><ChevronLeft size={14} /></Button>
                <Select value={String(year)} onValueChange={(v) => setYear(parseInt(v))}>
                  <SelectTrigger className="w-[110px] h-9" data-testid="revenue-year-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {years.map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button variant="outline" size="sm" onClick={() => setYear(y => y + 1)} data-testid="revenue-year-next"><ChevronRight size={14} /></Button>
              </div>
              <Button
                variant="outline"
                onClick={() => downloadCsv("/revenue/monthly.csv", { year }, `revenue_${year}.csv`)}
                className="h-10"
                data-testid="monthly-csv-btn"
              >
                <Download size={14} /> Export CSV
              </Button>
            </div>

            {tab === "monthly" && (
            <>
            <div className="p-5 grid grid-cols-1 lg:grid-cols-3 gap-5">
              <Card className="lg:col-span-2 p-5 shadow-none border-slate-200">
                <div className="flex items-center justify-between mb-5">
                  <div>
                    <h3 className="font-heading font-semibold text-slate-900">Monthly Revenue</h3>
                    <div className="text-xs text-slate-500 mt-0.5">Accommodation + Consumptions · {year}</div>
                  </div>
                  {bestMonth && bestMonth.total > 0 && (
                    <div className="text-xs text-slate-500">Best: <span className="font-semibold text-slate-900">{bestMonth.label}</span> · {fmt(bestMonth.total)}</div>
                  )}
                </div>
                <div className="h-[280px]" data-testid="revenue-bar-chart">
                  {chartsReady && (
                  <ResponsiveContainer width="100%" height="100%" debounce={50}>
                    <BarChart data={data.months} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                      <XAxis dataKey="label" stroke="#64748B" fontSize={12} />
                      <YAxis stroke="#64748B" fontSize={12} tickFormatter={(v) => `€${v}`} />
                      <Tooltip formatter={(v) => fmt(v)} contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 13 }} cursor={{ fill: "#F8FAFC" }} />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      <Bar dataKey="accommodation" name="Accommodation" stackId="a" fill="#2563EB" />
                      <Bar dataKey="consumption" name="Consumptions" stackId="a" fill="#60A5FA" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                  )}
                </div>
              </Card>

              <Card className="p-5 shadow-none border-slate-200">
                <div className="mb-5">
                  <h3 className="font-heading font-semibold text-slate-900">Payments Collected</h3>
                  <div className="text-xs text-slate-500 mt-0.5">Cash flow by month</div>
                </div>
                <div className="h-[280px]" data-testid="revenue-line-chart">
                  {chartsReady && (
                  <ResponsiveContainer width="100%" height="100%" debounce={50}>
                    <LineChart data={data.months} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                      <XAxis dataKey="label" stroke="#64748B" fontSize={11} />
                      <YAxis stroke="#64748B" fontSize={11} tickFormatter={(v) => `€${v}`} />
                      <Tooltip formatter={(v) => fmt(v)} contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 13 }} />
                      <Line type="monotone" dataKey="payments" stroke="#10B981" strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                    </LineChart>
                  </ResponsiveContainer>
                  )}
                </div>
              </Card>
            </div>

            <Table data-testid="revenue-monthly-table">
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>Month</TableHead>
                  <TableHead className="text-center">Reservations</TableHead>
                  <TableHead className="text-right">Accommodation</TableHead>
                  <TableHead className="text-right">Consumptions</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead className="text-right">Payments</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(data.months || []).map(m => (
                  <TableRow key={m.month} className="border-slate-100">
                    <TableCell className="font-medium text-slate-900">{m.label}</TableCell>
                    <TableCell className="text-center text-slate-600"><span className="inline-flex items-center gap-1"><Users size={12} className="text-slate-400" />{m.reservations}</span></TableCell>
                    <TableCell className="text-right text-slate-900">{fmt(m.accommodation)}</TableCell>
                    <TableCell className="text-right text-slate-900">{fmt(m.consumption)}</TableCell>
                    <TableCell className="text-right font-semibold text-slate-900">{fmt(m.total)}</TableCell>
                    <TableCell className="text-right text-emerald-700">{fmt(m.payments)}</TableCell>
                  </TableRow>
                ))}
                <TableRow className="bg-slate-50 font-semibold border-t-2 border-slate-200">
                  <TableCell className="text-slate-900">{year} Total</TableCell>
                  <TableCell className="text-center text-slate-900">{data.totals?.reservations || 0}</TableCell>
                  <TableCell className="text-right text-slate-900">{fmt(data.totals?.accommodation || 0)}</TableCell>
                  <TableCell className="text-right text-slate-900">{fmt(data.totals?.consumption || 0)}</TableCell>
                  <TableCell className="text-right font-heading text-lg text-blue-600">{fmt(data.totals?.total || 0)}</TableCell>
                  <TableCell className="text-right text-emerald-700">{fmt(data.totals?.payments || 0)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
            </>
            )}

          </TabsContent>
        </Tabs>
      </Card>

      <ReservationDrawer
        reservationId={selectedRes}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onChanged={() => getRevenueByGuest(guestQueryParams).then(setGuestData)}
      />
    </div>
  );
}
